'use client';

/**
 * The guest table page's data hooks (Guest QR Platform, Task 7).
 *
 * `useTableCheck` keeps a guest's view of their own bill current while they are
 * actually looking at it. There is no realtime channel here on purpose: the
 * public tables route is unauthenticated (the table token IS the credential),
 * so it is an HTTP poll behind a rate limit rather than a Firestore listener a
 * signed-out browser could not open anyway.
 *
 * ── The pattern ─────────────────────────────────────────────────────────────
 * React Query `refetchInterval` + `refetchIntervalInBackground: false`, the
 * same shape `useIssueDeliveries` and `useOfflineQueueStatus` already use.
 * The visibility gate is EXPLICIT on top of that rather than left to the
 * library's focus manager, for two reasons: the interval must be provably
 * `false` while hidden (a test can read the option; it cannot read the focus
 * manager's mind), and a tab becoming visible must trigger a refetch
 * IMMEDIATELY — a guest taking their phone back out is the moment the bill
 * matters most, and waiting up to 15 seconds for the next tick shows them a
 * stale total while a server rings in a round of drinks.
 *
 * ── Why the gate is worth having ────────────────────────────────────────────
 * Six guests at one table leave this page open for the length of a meal. At 15
 * seconds unconditionally that is ~1,440 requests an hour from one table, on
 * one wifi IP, against a route whose limit is 120/min for that pair — for a
 * bill nobody is looking at, with six phones' batteries paying for it.
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import type { GuestCheckView } from '@order-up/common';

import { getTableCheck, isTableMiss } from '../services/publicTables';

/** React Query key factory for everything keyed by a table TOKEN. */
export const guestTableKeys = {
  all: ['guestTable'] as const,
  check: (token: string) => [...guestTableKeys.all, 'check', token] as const,
};

/** How often the bill is re-read while the guest is looking at the page. */
export const TABLE_CHECK_POLL_MS = 15_000;

/**
 * `true` while the document is visible. SSR-safe (no `document` on the server),
 * and it reads the CURRENT value on mount rather than assuming visible — a page
 * restored into a background tab starts paused.
 */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() =>
    typeof document === 'undefined' ? true : document.visibilityState === 'visible',
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const sync = () => setVisible(document.visibilityState === 'visible');
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);

  return visible;
}

/**
 * The query options for a table's check, as a pure function of the token and
 * whether the page is visible.
 *
 * Separated out so the poll's central claim — *no interval while hidden* — is
 * something a test can assert directly, instead of inferring it from timers.
 */
export function tableCheckQueryOptions({
  token,
  visible,
}: {
  token: string;
  visible: boolean;
}): UseQueryOptions<GuestCheckView | null, Error> {
  return {
    queryKey: guestTableKeys.check(token),
    queryFn: () => getTableCheck(token),
    enabled: !!token,
    refetchInterval: visible ? TABLE_CHECK_POLL_MS : false,
    refetchIntervalInBackground: false,
    // OFF, so the visibility gate below is the ONLY thing that triggers a
    // refetch. React Query's focusManager fires on the same `visibilitychange`
    // event this hook listens to, so leaving it on sends two requests for one
    // moment of the guest picking their phone back up — and makes the gate
    // untestable, because a passing test could be either mechanism.
    refetchOnWindowFocus: false,
    // A bill is never fresh. Anything above zero shows a guest a total that a
    // server has already changed.
    staleTime: 0,
    // A miss means the token stopped being live (replaced, or the page closed).
    // Retrying that is a request that is guaranteed to fail the same way.
    retry: (failureCount, error) => !isTableMiss(error) && failureCount < 2,
  };
}

/**
 * A table's current bill, or `null` when it has none.
 *
 * An error is NOT surfaced as data: the card renders nothing rather than an
 * error box. A guest can do nothing about a failed read of their bill, the
 * paper bill is three feet away, and an alarming red box on a table page is a
 * worse outcome than an absent card.
 */
export function useTableCheck(token: string) {
  const visible = useDocumentVisible();
  const query = useQuery(tableCheckQueryOptions({ token, visible }));

  // Fire on the hidden → visible EDGE only. Keyed on the transition rather than
  // on `visible` itself, or the mount would fetch twice.
  const { refetch } = query;
  const wasVisible = useRef(visible);
  useEffect(() => {
    if (visible && !wasVisible.current) void refetch();
    wasVisible.current = visible;
  }, [visible, refetch]);

  return query;
}
